/**
 * A window on the Desktop — a folder's contents, the Trash, or a file
 * viewer. Non-modal (`role="dialog"`, `aria-modal="false"`): several can be
 * open, and the desktop behind stays usable.
 *
 * Drag the title bar to move it; drag the right or bottom edge, or the
 * corner, to resize. Changes are drawn at once and committed (saved to the
 * person's arrangement) when the pointer is released — and the contents
 * reflow live while resizing. What's drawn is the saved geometry clamped
 * into the visible area — never written back, so a small screen can't shrink
 * the big-monitor arrangement. Escape closes the focused window; focus goes
 * back to the icon that opened it.
 *
 * Chrome follows the desktop style: Windows puts the icon and title on the
 * left and minimise / maximise / close on the right, with a command bar
 * below; a Mac window has the traffic lights on the left and a unified
 * toolbar (and may have a sidebar and a status bar).
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Copy, Minus, Square, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MIN_WINDOW, clampWindow, type WindowGeometry } from '../../lib/desktop-layout';
import type { DesktopStyle } from '../../lib/folder-view';

export interface FolderWindowProps {
  id: string;
  title: string;
  icon: ReactNode;
  /** Saved geometry; drawn clamped into `bounds`. */
  geometry: WindowGeometry;
  bounds: { width: number; height: number };
  /** CSS stacking order among open windows. */
  zIndex: number;
  focused: boolean;
  /** Bumped when the window is opened or raised from outside — focuses the title bar. */
  focusRequest: number;
  onFocus: () => void;
  onCommit: (geometry: WindowGeometry) => void;
  onMinimize: () => void;
  onClose: () => void;
  /** Fill the desktop, or restore (Windows maximise / Mac zoom). */
  onZoom?: () => void;
  /** Drawn as filling the desktop right now. */
  zoomed?: boolean;
  style: DesktopStyle;
  /** What the window shows, for keyboard paste (`root`, a folder id, `trash`). */
  folder?: string;
  /** Windows: the command bar under the title bar. Mac: the unified toolbar
   *  in the title bar, after the traffic lights. */
  toolbar?: ReactNode;
  /** Mac: the sidebar down the left. */
  sidebar?: ReactNode;
  /** A status line along the bottom (item counts; the Mac icon-size slider). */
  statusBar?: ReactNode;
  children: ReactNode;
}

type Gesture = {
  kind: 'move' | 'right' | 'bottom' | 'corner';
  x: number;
  y: number;
  start: WindowGeometry;
};

export function FolderWindow(props: FolderWindowProps) {
  const { geometry, bounds } = props;
  const titleId = useId();
  const titleRef = useRef<HTMLDivElement>(null);
  const [live, setLive] = useState<WindowGeometry | null>(null);
  const liveRef = useRef<WindowGeometry | null>(null);
  const gesture = useRef<Gesture | null>(null);
  const drawn = clampWindow(live ?? geometry, bounds);

  useEffect(() => {
    if (props.focusRequest > 0) titleRef.current?.focus({ preventScroll: true });
  }, [props.focusRequest]);

  const begin = (kind: Gesture['kind']) => (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('button, input, select, [role="menu"], [role="slider"]'))
      return;
    e.preventDefault();
    e.stopPropagation();
    props.onFocus();
    gesture.current = { kind, x: e.clientX, y: e.clientY, start: drawn };
    const move = (ev: PointerEvent) => {
      const g = gesture.current;
      if (!g) return;
      const dx = ev.clientX - g.x;
      const dy = ev.clientY - g.y;
      const next = { ...g.start };
      if (g.kind === 'move') {
        next.x = g.start.x + dx;
        next.y = g.start.y + dy;
      }
      if (g.kind === 'right' || g.kind === 'corner')
        next.w = Math.max(MIN_WINDOW.w, g.start.w + dx);
      if (g.kind === 'bottom' || g.kind === 'corner')
        next.h = Math.max(MIN_WINDOW.h, g.start.h + dy);
      liveRef.current = next;
      setLive(next);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      gesture.current = null;
      const finished = liveRef.current;
      liveRef.current = null;
      setLive(null);
      if (finished) props.onCommit(clampWindow(finished, bounds));
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const mac = props.style === 'mac';
  const titleBarClass = cn(
    'flex shrink-0 cursor-grab select-none items-center outline-none active:cursor-grabbing',
    'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
  );

  return (
    <section
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      data-desktop-window={props.id}
      data-desktop-folder={props.folder}
      onPointerDownCapture={() => {
        if (!props.focused) props.onFocus();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !e.defaultPrevented) {
          e.preventDefault();
          e.stopPropagation();
          props.onClose();
        }
      }}
      className={cn(
        'absolute flex flex-col overflow-hidden border bg-card',
        mac ? 'rounded-xl' : 'rounded-lg',
        props.focused
          ? mac
            ? 'border-black/15 shadow-[0_22px_70px_rgba(0,0,0,0.35)] dark:border-white/15'
            : 'border-primary/40 shadow-2xl'
          : mac
            ? 'border-black/10 shadow-lg dark:border-white/10'
            : 'border-border shadow-xl',
      )}
      style={{ left: drawn.x, top: drawn.y, width: drawn.w, height: drawn.h, zIndex: props.zIndex }}
    >
      {mac ? (
        <div
          ref={titleRef}
          tabIndex={0}
          onPointerDown={begin('move')}
          onDoubleClick={(e) => {
            if (!(e.target as HTMLElement).closest('button, input, [role="menu"]'))
              props.onZoom?.();
          }}
          className={cn(
            titleBarClass,
            'h-11 gap-3 border-b border-black/10 px-3 dark:border-white/10',
            props.focused ? 'bg-muted/80' : 'bg-muted/50',
          )}
          style={{ touchAction: 'none' }}
        >
          <TrafficLights
            title={props.title}
            focused={props.focused}
            onClose={props.onClose}
            onMinimize={props.onMinimize}
            onZoom={props.onZoom}
          />
          {props.toolbar ? (
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <h2 id={titleId} className="sr-only">
                {props.title}
              </h2>
              {props.toolbar}
            </div>
          ) : (
            <h2
              id={titleId}
              className="min-w-0 flex-1 truncate text-center text-[13px] font-semibold text-foreground/85"
            >
              {props.title}
            </h2>
          )}
        </div>
      ) : (
        <>
          <div
            ref={titleRef}
            tabIndex={0}
            onPointerDown={begin('move')}
            onDoubleClick={(e) => {
              if (!(e.target as HTMLElement).closest('button')) props.onZoom?.();
            }}
            className={cn(
              titleBarClass,
              'gap-2 border-b pl-2.5',
              props.focused ? 'bg-muted/70' : 'bg-muted/40',
            )}
            style={{ touchAction: 'none' }}
          >
            <span className="shrink-0">{props.icon}</span>
            <h2
              id={titleId}
              className="min-w-0 flex-1 truncate py-1.5 text-sm font-medium text-foreground"
            >
              {props.title}
            </h2>
            <div className="flex self-stretch">
              <button
                type="button"
                aria-label={`Minimize ${props.title}`}
                title="Minimize"
                onClick={props.onMinimize}
                className="flex w-10 items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Minus className="h-4 w-4" />
              </button>
              {props.onZoom && (
                <button
                  type="button"
                  aria-label={props.zoomed ? `Restore ${props.title}` : `Maximize ${props.title}`}
                  title={props.zoomed ? 'Restore down' : 'Maximize'}
                  onClick={props.onZoom}
                  className="flex w-10 items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {props.zoomed ? (
                    <Copy className="h-3.5 w-3.5" />
                  ) : (
                    <Square className="h-3.5 w-3.5" />
                  )}
                </button>
              )}
              <button
                type="button"
                aria-label={`Close ${props.title}`}
                title="Close (Esc)"
                onClick={props.onClose}
                className="flex w-10 items-center justify-center text-muted-foreground hover:bg-red-600 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          {props.toolbar && (
            <div className="shrink-0 border-b border-border bg-card px-2 py-1">{props.toolbar}</div>
          )}
        </>
      )}
      <div className="relative flex min-h-0 flex-1">
        {props.sidebar && (
          <nav
            aria-label="Sidebar"
            className="w-40 shrink-0 overflow-y-auto border-r border-black/10 bg-muted/40 py-2 text-[13px] dark:border-white/10"
          >
            {props.sidebar}
          </nav>
        )}
        <div className="relative min-w-0 flex-1">{props.children}</div>
      </div>
      {props.statusBar && (
        <div
          className={cn(
            'flex shrink-0 items-center gap-3 border-t px-3 text-[11px] text-muted-foreground',
            mac
              ? 'h-7 border-black/10 bg-muted/50 dark:border-white/10'
              : 'h-6 border-border bg-card',
          )}
        >
          {props.statusBar}
        </div>
      )}

      {/* Resize handles — generous, so a finger can find them. */}
      <div
        aria-hidden
        onPointerDown={begin('right')}
        className="absolute bottom-3 right-0 top-10 w-2 cursor-ew-resize"
        style={{ touchAction: 'none' }}
      />
      <div
        aria-hidden
        onPointerDown={begin('bottom')}
        className="absolute bottom-0 left-0 right-3 h-2 cursor-ns-resize"
        style={{ touchAction: 'none' }}
      />
      <div
        aria-hidden
        onPointerDown={begin('corner')}
        className="absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize"
        style={{ touchAction: 'none' }}
      />
    </section>
  );
}

/** A Mac window's close / minimise / zoom buttons (glyphs appear on hover). */
function TrafficLights({
  title,
  focused,
  onClose,
  onMinimize,
  onZoom,
}: {
  title: string;
  focused: boolean;
  onClose: () => void;
  onMinimize: () => void;
  onZoom?: () => void;
}) {
  const light =
    'group/light flex h-3 w-3 items-center justify-center rounded-full border border-black/15 text-[9px] font-bold leading-none text-black/60';
  return (
    <div
      className="group flex shrink-0 items-center gap-2"
      onPointerDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        aria-label={`Close ${title}`}
        title="Close"
        onClick={onClose}
        className={cn(light, focused ? 'bg-[#ff5f57]' : 'bg-muted-foreground/30')}
      >
        <span className="opacity-0 group-hover:opacity-100">×</span>
      </button>
      <button
        type="button"
        aria-label={`Minimize ${title}`}
        title="Minimize"
        onClick={onMinimize}
        className={cn(light, focused ? 'bg-[#febc2e]' : 'bg-muted-foreground/30')}
      >
        <span className="opacity-0 group-hover:opacity-100">−</span>
      </button>
      <button
        type="button"
        aria-label={`Zoom ${title}`}
        title="Zoom"
        onClick={onZoom}
        disabled={!onZoom}
        className={cn(light, focused ? 'bg-[#28c840]' : 'bg-muted-foreground/30')}
      >
        <span className="opacity-0 group-hover:opacity-100">+</span>
      </button>
    </div>
  );
}
